import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import {
  ApiError,
  completeOidcLink,
  getAuthProviders,
  getPendingOidcLink,
  oidcStartUrl,
  type AuthProviders,
  type PendingOidcLink,
} from './api.js';
import { useSession } from './session.js';
import { Button, Field, Input } from '../design-system/index.js';

/**
 * The only unauthenticated page.
 *
 * ── THE MFA STEP IS A SECOND STATE, NOT A SECOND FORM ──────────────────────
 * `POST /auth/login` answers **428 `mfa_required`** when the account has a second factor and
 * no code was sent — a distinct status from the 401 a wrong password gets, deliberately, so
 * the client can tell "you need a code" from "that was wrong". This form therefore submits
 * email and password first and grows a TOTP field on a 428, keeping the same form element and
 * the same submit handler. A separate page would lose the password, which the API needs again
 * on the second request.
 *
 * ── AND THE ERROR IS A LIVE REGION WITH `alert`, NOT A RED PARAGRAPH ───────
 * A validation message that appears after an async round trip is not read by a screen reader
 * unless it is in a live region: focus is still on the submit button and nothing about the
 * DOM change is announced. `role="alert"` is `aria-live="assertive"` plus `aria-atomic`, which
 * is right here — a failed sign-in is worth interrupting for, and there is nothing else being
 * read at that moment.
 *
 * ── SINGLE SIGN-ON IS A LINK ABOVE THE FORM, NOT A SECOND FORM ───────────────
 * When `GET /auth/providers` says the install has an identity provider, a "Sign in with …" link
 * appears first. It is an anchor to `/api/auth/oidc/start`: the API answers 302 to the provider
 * and the provider sends the browser back signed in (`api/src/routes/auth/oidc.ts`). A button
 * that `fetch`ed it would get the redirect and nowhere to go. The password form stays underneath
 * for the account that must work when the provider does not. A refused single sign-on comes back
 * to this page as `?error=oidc_<reason>`, and is announced through the same live region.
 *
 * ── AND "CONNECT YOUR ACCOUNT" IS THE SAME FORM WITH THE E-MAIL ALREADY KNOWN ─
 * An account that exists here but was never signed into through the provider comes back as
 * `?link=1`: the callback parked the identity and the person proves the account ONCE with its
 * console password (and code) — the proof that binds the two (`api/src/auth/oidc.ts`, "Why an
 * e-mail match alone never binds"). It is this form minus the e-mail field, submitting to
 * `completeOidcLink` instead of `login`; the 428 step and the live region are shared.
 */
export function LoginPage(): ReactNode {
  const { login } = useSession();
  const navigate = useNavigate();

  // Read once, at mount: the router put `redirect` here (`router.tsx`), the API puts `error` and `link` here.
  const [{ redirectTo, ssoError, linking }] = useState(() => readLoginSearch(window.location.search));
  /** `undefined` while asking, `null` when there is nothing to connect (expired), else the account. */
  const [pendingLink, setPendingLink] = useState<PendingOidcLink | null | undefined>(linking ? undefined : null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | undefined>(ssoError);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<AuthProviders | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void getAuthProviders().then((answer) => {
      if (live) setProviders(answer);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!linking) return;
    let live = true;
    void getPendingOidcLink().then((pending) => {
      if (!live) return;
      setPendingLink(pending ?? null);
      // Nothing waiting: the ten minutes passed, or the page was opened by hand. Say so, and the
      // ordinary form underneath is the way on.
      if (!pending) setError(SSO_ERRORS['oidc_expired']);
    });
    return () => {
      live = false;
    };
  }, [linking]);

  const connecting = pendingLink !== null && pendingLink !== undefined;

  const totpRef = useRef<HTMLInputElement>(null);

  /**
   * Move focus to the code field the moment it appears.
   *
   * A field that materialises below the button the user just pressed is invisible to anyone
   * not looking at the screen, and a sighted keyboard user would have to shift-tab backwards
   * into it. Focusing it also causes a screen reader to announce its label, which is how the
   * "now enter your code" instruction gets delivered without a second live region.
   */
  useEffect(() => {
    if (needsTotp) totpRef.current?.focus();
  }, [needsTotp]);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const code = needsTotp && totp !== '' ? { totp } : {};
      if (connecting) await completeOidcLink({ password, ...code });
      else await login({ email, password, ...code });
      await navigate({ to: '/' });
    } catch (caught) {
      if (caught instanceof ApiError && caught.needsMfa) {
        setNeedsTotp(true);
        // NOT an error: the credentials were accepted and a code is now wanted. Rendering
        // this in the alert region would tell the user they got their password wrong.
        return;
      }
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reach the server. Check your connection and try again.',
      );
      // A wrong TOTP means the code was wrong, not the password — keep the field and clear it.
      if (needsTotp) setTotp('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-dvh-safe items-center justify-center bg-canvas px-4 py-12">
      {/* `min-h-dvh-safe`: `100dvh`, with `100vh` before it for engines without `dvh`. On a phone,
          `vh` counts the space under the browser's own bars, so the card sat low and the page
          scrolled for no reason. */}
      <div className="w-full max-w-sm">
        <h1 translate="no" className="mb-1 text-xl font-semibold tracking-tight text-fg">
          Orcologic PBX
        </h1>
        {pendingLink ? (
          <p className="mb-6 text-sm text-fg-muted">
            <span className="block font-medium text-fg">Connect your {pendingLink.provider} sign-in</span>
            You signed in to {pendingLink.provider} as <span className="font-medium text-fg">{pendingLink.email}</span>.
            Enter this console&apos;s password once to connect the two; from then on {pendingLink.provider} alone
            signs you in.
          </p>
        ) : (
          <p className="mb-6 text-sm text-fg-muted">Sign in to the console.</p>
        )}

        {providers?.oidc.enabled && !pendingLink ? (
          <div className="mb-4 space-y-3">
            {/*
              `asChild`: the Button's look on a real <a>. The destination is a page the API
              redirects, so this has to be a navigation the browser performs itself.
            */}
            <Button asChild variant="primary" size="lg" className="w-full">
              <a href={oidcStartUrl(redirectTo)}>Sign in with {providers.oidc.displayName}</a>
            </Button>
            <p className="text-center text-xs text-fg-muted">or with your console password</p>
          </div>
        ) : null}

        <form
          onSubmit={(event) => void onSubmit(event)}
          /**
           * `noValidate` so the browser's own bubble does not pre-empt the form. Native
           * validation messages are not keyboard-dismissible in every engine and cannot be
           * styled to meet contrast; the constraints are still declared on the inputs, so
           * assistive technology still reads `required` and `type`.
           */
          noValidate
          className="space-y-4 rounded-lg border border-border bg-surface p-6 shadow-sm"
        >
          {/*
            No render prop: `Input` reads the field's ids from context (`useFieldControl`), so
            the label, the description and the `aria-describedby` wiring happen without this
            page restating them. The render-prop form exists for controls that cannot read the
            context — a bare `<textarea>`, a third-party widget — and using it here would put
            four attributes in the diff that nothing checks.
          */}
          {connecting ? null : (
            <Field label="Email" required>
              <Input
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={needsTotp}
              />
            </Field>
          )}

          <Field label="Password" required>
            <Input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={needsTotp}
            />
          </Field>

          {needsTotp ? (
            <Field
              label="Authentication code"
              description="The six-digit code from your authenticator app."
              required
            >
              <Input
                ref={totpRef}
                name="totp"
                /**
                 * `inputMode="numeric"` and not `type="number"`: a number input strips leading
                 * zeros, which silently mangles a code like `012345` into `12345` and produces
                 * a rejection the user cannot explain. `one-time-code` is what lets iOS and
                 * Android offer the code straight from the notification.
                 */
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
          ) : null}

          {error ? (
            <p role="alert" className="rounded-md bg-danger-subtle px-3 py-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" size="lg" loading={busy} loadingLabel="Signing in…" className="w-full">
            {needsTotp ? 'Verify code' : connecting ? 'Connect and sign in' : 'Sign in'}
          </Button>
        </form>
      </div>
    </main>
  );
}

/** What the login page says for each `?error=oidc_<reason>` the API can send it back with. */
const SSO_ERRORS: Readonly<Record<string, string>> = {
  oidc_denied: 'The single sign-on was cancelled or refused by the identity provider.',
  oidc_expired: 'The single sign-on took too long or was already used. Try again.',
  oidc_failed: 'The single sign-on could not be completed. Try again, or sign in with your password.',
  oidc_incomplete: 'The identity provider sent no e-mail address for your account, so it cannot be matched here.',
  oidc_not_in_group: 'Your account is not allowed into the phone system. Ask an administrator.',
  oidc_no_account: 'There is no console account for your e-mail address. Ask an administrator to create one.',
  oidc_subject_mismatch:
    'Your e-mail address is already linked to a different identity-provider account. Ask an administrator.',
  oidc_unavailable: 'The single sign-on service is unavailable right now. Sign in with your password.',
};

/**
 * The two things the URL can tell this page. `redirect` is passed through to the API, which
 * keeps only a path on this origin; it is never navigated to from here.
 */
export function readLoginSearch(search: string): { redirectTo: string; ssoError: string | undefined; linking: boolean } {
  const params = new URLSearchParams(search);
  const redirect = params.get('redirect') ?? '';
  const code = params.get('error') ?? '';
  return {
    redirectTo: redirect === '' ? '/' : redirect,
    ssoError: code === '' ? undefined : SSO_ERRORS[code] ?? SSO_ERRORS['oidc_failed'],
    linking: params.get('link') === '1',
  };
}
